import BankCardWarningIcon from "@/assets/colorable/bank_card_warning.svg?react";
import SignOutIcon from "@/assets/colorable/sign_out.svg?react";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import PaymentFailedContent from "@/components/Common/payment/PaymentFailedContent";
import TextButton from "@/components/Common/TextButton";
import {useFailedInvoicePayment} from "@/hooks/useFailedInvoicePayment";
import {useLogout} from "@/hooks/useLogout";
import {getPaymentDeadline} from "@/hooks/usePayment";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import {useSelectedCompany} from "@/hooks/useUserHooks";
import {Can} from "@/permissions";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {hasClosedPaymentFailedModalAtom, paymentFailedModalDataAtom} from "@/store/jotaiAtoms";
import dayjs from "dayjs";
import {useAtom, useSetAtom} from "jotai";
import type {FC} from "react";
import {useTranslation} from "react-i18next";
import {useEffectOnce} from "react-use";

const PaymentFailedModal: FC = () => {
  const {t} = useTranslation("common");
  const logout = useLogout();
  const selectedCompany = useSelectedCompany();
  const formatPrice = usePriceFormat();
  const setHasClosedPaymentFailedModal = useSetAtom(hasClosedPaymentFailedModalAtom);
  const [state, setState] = useAtom(paymentFailedModalDataAtom);
  const paymentDeadline = getPaymentDeadline(state ?? []);
  const shouldHideCloseButton = !!paymentDeadline && dayjs().isAfter(paymentDeadline);
  const payment = useFailedInvoicePayment({invoices: state ?? [], onPaid: () => setState(null)});

  useEffectOnce(() => {
    capturePostHogEvent(PostHogEvents.PaymentFailedModalViewed, {
      currency: payment.currency,
      invoiceCount: state?.length,
      totalAmount: payment.totalAmount,
    });
  });

  return (
    <Modal
      shouldHideCloseButton={shouldHideCloseButton}
      onClose={() => {
        setState(null);
        setHasClosedPaymentFailedModal(true);
      }}
      customTitleRow={
        <div className="flex items-center gap-2 p-5">
          <BankCardWarningIcon className="text-danger-medium-red h-auto w-6 shrink-0" />
          <span className="text-neutral-black text-xl font-semibold">{t("failedToProcessPayment")}</span>
        </div>
      }
      customBottomRow={
        <div className="border-neutral-grey-light-dark flex flex-wrap items-center gap-4 border-t p-5">
          <div className="flex flex-col gap-1">
            <span className="text-neutral-grey-dark text-xs">{t("amount_due")}</span>
            <span className="text-black-bluetint text-3xl font-semibold">{formatPrice(payment.totalAmount)}</span>
          </div>
          <Can I="list" a="CompanyPaymentMethod">
            <Button
              onClick={payment.pay}
              buttonStyle="Gradient"
              isDisabled={payment.isPayDisabled}
              className="ml-auto min-w-75 px-8 py-3">
              {t("pay")}
            </Button>
          </Can>
        </div>
      }
      backgroundClassName="max-w-210">
      <PaymentFailedContent
        billingDetails={{
          city: selectedCompany?.address_city,
          country: selectedCompany?.country,
          postalCode: selectedCompany?.address_postal,
          street: selectedCompany?.address_street,
        }}
        invoices={state ?? []}
        payment={payment}
      />
      {shouldHideCloseButton && (
        <div className="mt-6 flex flex-col gap-6">
          <Button
            buttonStyle="Blue"
            className="h-auto w-fit gap-2 px-8 py-3"
            onClick={() => {
              logout();
              setState(null);
            }}>
            <SignOutIcon />
            {t("clickToLogout")}
          </Button>
          <Can I="create" a="MailToSupport">
            <span className="text-neutral-black text-center text-sm">
              {t("needHelp")} {t("contactOurAdministrator")}:{" "}
              <TextButton
                onClick={() => (window.location.href = `mailto:info@refluenced.com?subject=${t("payment_failed")}`)}
                text="Refluenced Support"
              />
            </span>
          </Can>
        </div>
      )}
    </Modal>
  );
};

export default PaymentFailedModal;
